import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { ColumnFilter } from '../ui/ColumnFilter';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import type { HoursAccount, HoursRow, HoursSummary, HoursViewer } from '../../types/timerocket';
import { formatDays, formatMinutes, formatWorkload } from './format';
import { DiscrepancyBadges } from './DiscrepancyBadges';

/**
 * One row per person for the period, plus one column per TimeRocket account with the balance
 * as it stood at the period's end. Table standard: sticky sortable headers, drag-resizable
 * columns, Excel-style column filters, sums over the FILTERED rows.
 *
 * The account columns are not known at build time — they are the tenant's catalog, delivered
 * with every response — so the column list is the fixed part at module scope plus the account
 * part memoised per catalog. Their headers are the tenant's own labels ("Ferien",
 * "Überstunden"), shown verbatim: translating them would break the link to what the person
 * sees in TimeRocket itself.
 *
 * A row opens the daily detail only for the person themself or for a canSeeAll viewer. A
 * supervisor's team rows are deliberately inert — no pointer, no hint — because the daily
 * stampings of a subordinate are not theirs to open (ArGV 3 Art. 26).
 */

type FixedKey =
    | 'name' | 'companyCode' | 'orgUnit' | 'workload' | 'days'
    | 'targetMin' | 'actualMin' | 'presenceMin' | 'balanceMin' | 'asOf';
type AccountKey = `acct:${string}`;
type ColKey = FixedKey | AccountKey;

interface ColDef {
    key: ColKey;
    label: string;
    width: number;
    align?: 'right';
    account?: HoursAccount;
}

const FIXED_LEFT: ColDef[] = [
    { key: 'name', label: 'Name', width: 200 },
    { key: 'companyCode', label: 'Company', width: 80 },
    { key: 'orgUnit', label: 'Org unit', width: 150 },
    { key: 'workload', label: 'Workload', width: 90, align: 'right' },
    { key: 'days', label: 'Days', width: 70, align: 'right' },
    { key: 'targetMin', label: 'Target', width: 90, align: 'right' },
    { key: 'actualMin', label: 'Actual', width: 90, align: 'right' },
    { key: 'presenceMin', label: 'Presence', width: 90, align: 'right' },
    { key: 'balanceMin', label: 'Balance', width: 90, align: 'right' },
];

const FIXED_RIGHT: ColDef[] = [
    { key: 'asOf', label: 'As of', width: 100 },
];

const FIXED_NUMERIC: FixedKey[] = ['workload', 'days', 'targetMin', 'actualMin', 'presenceMin', 'balanceMin'];

const FILTER_LABELS = { search: 'Search…', all: 'All', none: 'None', from: 'from', to: 'to', empty: '(blank)' };

const VACATION_SEMANTIC = 'FERIEN';

/** The balance date shown per row: the vacation balance's, else the latest of any account. */
function asOfFor(row: HoursRow, vacationAccountId: string | null): string | null {
    const vac = vacationAccountId ? row.balances[vacationAccountId] : undefined;
    if (vac) return vac.asOfDate;
    let max: string | null = null;
    for (const b of Object.values(row.balances)) {
        if (max === null || b.asOfDate > max) max = b.asOfDate;
    }
    return max;
}

/** What an account cell sorts and filters on: its display unit (days where shown as days, else minutes). */
function accountValue(row: HoursRow, account: HoursAccount): number | null {
    const b = row.balances[account.accountId];
    if (!b) return null;
    if (account.showAsDays) return b.days;
    return b.totalMin;
}

const FIXED_ACCESSOR: Record<FixedKey, (r: HoursRow, vacationAccountId: string | null) => string | number | null> = {
    name: r => r.name,
    companyCode: r => r.companyCode?.toUpperCase() ?? null,
    orgUnit: r => r.orgUnit,
    workload: r => r.workload,
    days: r => r.days,
    targetMin: r => r.targetMin,
    actualMin: r => r.actualMin,
    presenceMin: r => r.presenceMin,
    balanceMin: r => r.balanceMin,
    asOf: (r, vac) => asOfFor(r, vac),
};

interface Props {
    rows: HoursRow[];
    accounts: HoursAccount[];
    summary: HoursSummary;
    viewer: HoursViewer;
    loading: boolean;
    /** Scope-specific text for "no rows at all" — the caller knows why the list is empty. */
    emptyMessage: string;
    onOpenDays: (row: HoursRow) => void;
}

export function HoursTable({ rows, accounts, summary, viewer, loading, emptyMessage, onOpenDays }: Props) {
    const [sort, setSort] = useState<SortState<ColKey>>({ key: 'name', direction: 'asc' });

    const sortedAccounts = useMemo(
        () => [...accounts].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)),
        [accounts],
    );
    const vacationAccountId = useMemo(
        () => sortedAccounts.find(a => (a.semantic ?? '').toUpperCase() === VACATION_SEMANTIC)?.accountId ?? null,
        [sortedAccounts],
    );

    const columns = useMemo<ColDef[]>(() => [
        ...FIXED_LEFT,
        ...sortedAccounts.map<ColDef>(a => ({ key: `acct:${a.accountId}`, label: a.label, width: 100, align: 'right', account: a })),
        ...FIXED_RIGHT,
    ], [sortedAccounts]);

    const accessor = useMemo(() => {
        const byKey = new Map<ColKey, (r: HoursRow) => string | number | null>();
        for (const c of columns) {
            if (c.account) {
                const account = c.account;
                byKey.set(c.key, r => accountValue(r, account));
            } else {
                const fixed = FIXED_ACCESSOR[c.key as FixedKey];
                byKey.set(c.key, r => fixed(r, vacationAccountId));
            }
        }
        return (key: ColKey) => byKey.get(key) ?? (() => null);
    }, [columns, vacationAccountId]);

    const isNumeric = (key: ColKey) => key.startsWith('acct:') || FIXED_NUMERIC.includes(key as FixedKey);

    const filterColumns = useMemo(
        () => columns.map(c => ({ key: c.key, accessor: accessor(c.key), numeric: isNumeric(c.key) })),
        [columns, accessor],
    );

    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(columns, 'timerocket-hours-col-widths');
    const { filtered, filters, setColumnFilter, isFiltered, activeCount, clearAll, uniqueValuesFor } =
        useColumnFilters(rows, filterColumns);

    // A drag ends with a mouseup inside the header cell, which the browser reports as a click.
    // Without this guard every resize would also re-sort the table.
    const onSort = (key: ColKey) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, isNumeric(key)));
    };

    // Sorting and totals happen over the FILTERED rows, so the footer never describes
    // something other than what is on screen.
    const sorted = sort.key
        ? [...filtered].sort((a, b) => {
            const acc = accessor(sort.key as ColKey);
            const av = acc(a);
            const bv = acc(b);
            if (av === null || av === undefined || av === '') return 1;
            if (bv === null || bv === undefined || bv === '') return -1;
            const base = typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv), undefined, { numeric: true });
            return base * (sort.direction === 'asc' ? 1 : -1);
        })
        : filtered;

    const sums = filtered.reduce(
        (s, r) => ({ target: s.target + r.targetMin, actual: s.actual + r.actualMin, presence: s.presence + r.presenceMin, balance: s.balance + r.balanceMin }),
        { target: 0, actual: 0, presence: 0, balance: 0 },
    );
    const vacationTotal = vacationAccountId === null ? null : filtered.reduce<number | null>((s, r) => {
        const d = r.balances[vacationAccountId]?.days;
        if (d === null || d === undefined) return s;
        return (s ?? 0) + d;
    }, null);

    // An HR-only row (no TimeRocket record, P4) has no daily detail to open.
    const canOpen = (row: HoursRow) => row.employeeId !== null && (viewer.canSeeAll || (viewer.employeeId !== null && row.employeeId === viewer.employeeId));

    const balanceClass = (min: number) => (min < 0 ? 'text-red-600' : '');

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            {activeCount > 0 && (
                <div className="flex items-center justify-between px-4 py-1.5 border-b bg-amber-50 text-xs text-amber-900">
                    <span>{activeCount} filter(s) active</span>
                    <button onClick={clearAll} className="text-blue-600 hover:text-blue-800 font-medium">Clear all</button>
                </div>
            )}

            {/* The sticky header needs a bounded scroll container or it does not stick. The floor is
                for the one-row own view: a filter popover (~300 px) opened over a 100 px table would
                be clipped by this very container, and the own view ALWAYS has one row. */}
            <div className="overflow-auto" style={{ maxHeight: '70vh', minHeight: rows.length > 0 && rows.length <= 3 ? 360 : undefined }}>
                {loading && rows.length === 0 && (
                    <div className="p-12 text-center text-gray-500">
                        <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…
                    </div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="p-8 text-center text-gray-500">{emptyMessage}</div>
                )}
                {rows.length > 0 && filtered.length === 0 && (
                    <div className="p-8 text-center text-gray-500">{activeCount} filter(s) active — no row matches.</div>
                )}
                {rows.length > 0 && filtered.length > 0 && (
                    <table className={`w-full text-sm ${loading ? 'opacity-60' : ''}`} style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                            {columns.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                        </colgroup>
                        <thead>
                            <tr className="border-b">
                                {columns.map(c => (
                                    <SortableHeader
                                        key={c.key}
                                        label={c.label}
                                        sortKey={c.key}
                                        sort={sort}
                                        onSort={onSort}
                                        align={c.align}
                                        onResizeStart={x => startResize(c.key, x)}
                                        onResetWidth={() => resetWidth(c.key)}
                                        isResizing={resizingKey === c.key}
                                        filter={
                                            <ColumnFilter
                                                label={c.label}
                                                values={uniqueValuesFor(c.key)}
                                                state={filters[c.key]}
                                                onChange={s => setColumnFilter(c.key, s)}
                                                numeric={isNumeric(c.key)}
                                                active={isFiltered(c.key)}
                                                labels={FILTER_LABELS}
                                            />
                                        }
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {sorted.map(r => {
                                const openable = canOpen(r);
                                return (
                                    <tr
                                        key={r.employeeId ?? `hr-${r.staffId}`}
                                        onClick={openable ? () => onOpenDays(r) : undefined}
                                        title={openable ? 'Open the daily detail' : undefined}
                                        className={openable ? 'cursor-pointer hover:bg-blue-50' : 'hover:bg-gray-50'}
                                    >
                                        <td className="px-3 py-1.5 font-medium truncate" title={r.name}>
                                            {r.name}
                                            {!r.isActive && <span className="ml-1 text-xs text-gray-400">(left)</span>}
                                            <DiscrepancyBadges items={r.discrepancies} />
                                        </td>
                                        <td className="px-3 py-1.5 text-gray-600">{r.companyCode?.toUpperCase() ?? '—'}</td>
                                        <td className="px-3 py-1.5 text-gray-600 truncate" title={r.orgUnit ?? undefined}>{r.orgUnit ?? '—'}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{formatWorkload(r.workload)}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">
                                            {r.days}
                                            {r.provisionalDays > 0 && (
                                                <span className="ml-0.5 text-amber-600" title="includes today's provisional value">•</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{formatMinutes(r.targetMin)}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{formatMinutes(r.actualMin)}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{formatMinutes(r.presenceMin)}</td>
                                        <td className={`px-3 py-1.5 text-right tabular-nums ${balanceClass(r.balanceMin)}`}>{formatMinutes(r.balanceMin)}</td>
                                        {sortedAccounts.map(a => {
                                            const b = r.balances[a.accountId];
                                            if (!b) return <td key={a.accountId} className="px-3 py-1.5 text-right text-gray-400">—</td>;
                                            if (a.showAsDays) {
                                                return b.days === null ? (
                                                    // No conversion rate: minutes are the only honest figure (trap 6).
                                                    <td key={a.accountId} className="px-3 py-1.5 text-right tabular-nums text-gray-500"
                                                        title="no conversion rate">
                                                        {b.totalMin} min
                                                    </td>
                                                ) : (
                                                    <td key={a.accountId} className={`px-3 py-1.5 text-right tabular-nums ${b.days < 0 ? 'text-red-600' : ''}`}>
                                                        {formatDays(b.days)}
                                                    </td>
                                                );
                                            }
                                            return (
                                                <td key={a.accountId} className={`px-3 py-1.5 text-right tabular-nums ${balanceClass(b.totalMin)}`}>
                                                    {formatMinutes(b.totalMin)}
                                                </td>
                                            );
                                        })}
                                        <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap">{asOfFor(r, vacationAccountId) ?? '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-gray-50 border-t font-medium">
                            <tr>
                                <td className="px-3 py-2" colSpan={5}>
                                    {activeCount > 0
                                        ? `${filtered.length} of ${rows.length} people`
                                        : `${summary.people} people`}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">{formatMinutes(sums.target)}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{formatMinutes(sums.actual)}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{formatMinutes(sums.presence)}</td>
                                <td className={`px-3 py-2 text-right tabular-nums ${balanceClass(sums.balance)}`}>{formatMinutes(sums.balance)}</td>
                                {sortedAccounts.map(a => (
                                    <td key={a.accountId} className="px-3 py-2 text-right tabular-nums"
                                        title={a.accountId === vacationAccountId ? 'Open vacation, days, over the rows with a conversion rate' : undefined}>
                                        {a.accountId === vacationAccountId && vacationTotal !== null ? formatDays(vacationTotal) : ''}
                                    </td>
                                ))}
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                )}
            </div>
        </div>
    );
}
